import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CAPTCHA_TYPE, CUSTOMER_CSTM_FIELD_TITLE_LIST, CUSTOM_FIELD_TYPES, EMAIL_REGEX, MEETING_TYPE_ID, SALUTATION_TYPES, WIDGET_TYPE, ZIP_REGEX } from '@constants/app.constants';
import { DATE_FORMAT, DATE_FORMATS } from '@constants/date.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { PhishingProtectionDirective } from '@directives/app-phising-protection.directive';
import { environment } from '@environments/environment';
import { FRCWidgetCompleteEvent, FRCWidgetWidgetErrorEvent, FriendlyCaptchaSDK, WidgetHandle } from "@friendlycaptcha/sdk";
import { CountryDbModel } from '@models/Country-db.model';
import { CustomFieldModel, CustomFieldOptionModel } from '@models/custom-field.model';
import { MeetingTypesModel } from '@models/meeting-types.model';
import { PartnerDbModel } from '@models/partner-db.model';
import { StoreDbModel } from '@models/store-db.model';
import { UserInfo } from '@models/user.model';
import { ZipCodeItem } from '@models/zip-code.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormatPipe } from '@pipes/address-format.pipe';
import { CalioAddRequiredPipe } from '@pipes/calio-add-required.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslateResolverPipe } from '@pipes/calio-translate-resolver.pipe';
import { CalioTranslationSortPipe } from '@pipes/calio-translation-sort.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { TrustHtmlPipe } from '@pipes/trust-html.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { CustomFieldsService } from '@services/feature/custom-fields.service';
import { WidgetService } from '@services/feature/widget.service';
import { CountryHelperService } from '@services/utils/country-helper.service';
import { FriendlyCaptchaFrame } from '@services/utils/friendly-captcha-frame.util';
import { clearWorkerSelectAnswer } from '@services/utils/custom-field-tree.util';
import { isZipPrefilledFromStorePostcode } from '@services/utils/store-postcode.util';
import { HelperService } from '@services/utils/helper.service';
import { HostPageScrollService } from '@services/utils/host-page-scroll.service';
import { LoggerService } from '@services/utils/logger.service';
import { BookingQuestionComponent } from '@ui-lib/booking-questions/booking-question/booking-question.component';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { CalioTelInputComponent } from '@ui-lib/calio-intl-tel-input/calio-tel-input/calio-tel-input.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import moment from 'moment';
import { Subject, debounceTime, distinctUntilChanged, filter, of, race, switchMap, take, timer } from 'rxjs';

const directives = [PhishingProtectionDirective];
const components = [CswTextComponent, CalioTelInputComponent, BookingQuestionComponent, ButtonComponent];
const modules = [FormsModule, NgSelectModule, NgClass, MatFormFieldModule, MatInputModule, MatDatepickerModule, TranslateModule];
const pipes = [CalioSafePipe, CalioAddRequiredPipe, CalioTranslationPipe, CalioMeetingTemplatePipe, TrustHtmlPipe, CalioTranslateResolverPipe, AddressFormatPipe, CalioTranslationSortPipe];

@Component({
  selector: 'app-personal-form',
  templateUrl: './personal-form.component.html',
  styleUrls: ['./personal-form.component.scss'],
  providers: [{
    provide: DateAdapter,
    useClass: MomentDateAdapter,
    deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
  }, {
    provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS
  }
  ],
  imports: [...directives, ...components, ...modules, ...pipes]
})
export class PersonalFormComponent implements OnInit {

  @Input() customerRequiredFields: string[] = [];
  // Per-widget required for custom questions: ids the partner marked required in THIS widget's
  // customer step (context.required_booking_questions). Additive — a question required by its own
  // definition stays required even when its id is absent here.
  @Input() requiredQuestionIds: number[] = [];
  @Input() customerReadonlyFields: string[] = [];
  @Input() customerPrefillValues: { [fieldName: string]: string } = {};
  @Input() userInfo = new UserInfo();
  @Input() customerFormFields: any[] = [];
  @Input() meetingTypes: number[] = [];
  @Input() partnerData: PartnerDbModel;
  @Input() selectedCustomFieldValues: {
    custom_field_id: number,
    type: string,
    value?: any, file_ids?: any
  }[] = [];
  @Input() showMeetingTypes = true;

  // [auto_load_booking_questions_placement=personal_form — review fix Finding A] Optional
  // pre-resolved auto-loaded question objects (BookingService.resolvedBookingQuestions()) so this
  // component doesn't redundantly re-fetch, via get_by_ids, ids the caller already resolved.
  // Signals-first per the angular-signals SKILL for this NEW input (the component itself stays
  // @Input()-decorator style — no wider migration). Default empty array: every existing caller
  // (dedicated_step, flag OFF) passes nothing, so ngOnInit()'s split below always finds zero
  // pre-resolved matches and falls through to the original single getCustomFieldsByIds(customFieldIds)
  // call, byte-for-byte (backward-compat).
  preResolvedQuestions = input<CustomFieldModel[]>([]);

  // The customer-entered store-search postcode. While the zip still holds it, the zip input carries
  // the `cbw-zip-prefilled` class, for partner custom CSS only (the widget does not style it). Default
  // null, so hosts that pass nothing (the lead generator) are unchanged.
  lockedZip = input<string | null>(null);

  @Output() submitEvent: EventEmitter<{
    userInfo: UserInfo,
    personalFormWidgetQuestions: CustomFieldModel[],
    hiddenCustomFields: CustomFieldModel[]
  }> = new EventEmitter<any>();
  @Output() previousEvent = new EventEmitter<any>();

  protected readonly emailRegex = EMAIL_REGEX;
  protected readonly imagePath = environment.imageHostName;
  protected readonly meetingTypeId = MEETING_TYPE_ID;
  protected readonly titleTypes = CUSTOMER_CSTM_FIELD_TITLE_LIST;
  protected readonly widgetTypes = WIDGET_TYPE;
  protected readonly captchaType = CAPTCHA_TYPE;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  protected countries: CountryDbModel[] = [];
  protected sortedCountries: CountryDbModel[] = [];
  protected errorMessage: string;
  protected errorTitle: string;
  protected fieldTypes: { type: string, value: any }[] = [];
  protected ghostElements = new Array<number>(8);
  protected hiddenCustomFields: CustomFieldModel[] = [];
  protected isSubmitButtonDisabled = false;
  // Guards the async validity-settle window in submit() against double-clicks.
  private validitySettleInFlight = false;
  protected lang: string;
  protected maximumPossibleDateForDatePicker = new Date();
  protected NOTIFICATION_TYPES: any[] = [];
  protected rawMeetingTypes: MeetingTypesModel[] = [];
  protected salutationTypes: any = SALUTATION_TYPES;
  protected selectedStore: StoreDbModel = null;
  protected showError = false;
  protected showGhostElement = true;
  protected showPersonalDataForm = false;
  protected widgetQuestions: { [key: number]: CustomFieldModel } = {};
  protected friendlyCaptchaSDK = new FriendlyCaptchaSDK();
  protected captchaWidgetHandle: WidgetHandle;
  protected captchaFrame: FriendlyCaptchaFrame;
  protected captchaLoaded = false;
  protected friendlyCaptchaValidationRequired = false
  protected supportedMeetingTypeIds = new Array<number>();
  protected readonly zipRegex = ZIP_REGEX;
  protected citySuggestions: ZipCodeItem[] = [];
  private readonly searchZip$ = new Subject<string>();
  private destroyRef = inject(DestroyRef);
  private hostPageScrollService = inject(HostPageScrollService);

  @ViewChild('friendlyCaptcha', { static: false }) friendlyCaptchaRef!: ElementRef;

  constructor(
    public widgetService: WidgetService,
    private customEventService: CustomEventService,
    private customFieldsService: CustomFieldsService,
    private helperService: HelperService,
    public bookingService: BookingService,
    private translate: TranslateService,
    private countryHelperService: CountryHelperService,
    private elementRef: ElementRef
  ) {
    this.customEventService.enableSubmitButtonEvent.subscribe(() => this.isSubmitButtonDisabled = false);

    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        language => {
          this.lang = language.lang;
          this.countries = this.countryHelperService.createNewCountries(this.countries);
        }
      );

    // Teardown: drop the captcha iframe from the FriendlyCaptcha bus (fixes the Safari null-contentWindow crash).
    this.destroyRef.onDestroy(() => {
      this.captchaWidgetHandle?.destroy();
      this.captchaFrame?.destroy();
    });
  }

  /** Maps a configured customer field name to the UserInfo property it seeds. */
  private readonly prefillFieldMap: { [field: string]: keyof UserInfo } = {
    prename: 'forename',
    lastname: 'lastName',
    email: 'eMail',
    phone: 'phone',
    mobile: 'mobile',
    company: 'company',
    salutation: 'gender',
    comment: 'comment',
  };

  /** True while the zip still holds the store-search postcode it was prefilled with. Drives the
   *  `cbw-zip-prefilled` class only; the field stays editable. */
  isZipPrefilled(): boolean {
    return isZipPrefilledFromStorePostcode(this.userInfo?.zip, this.lockedZip());
  }

  /** True when the partner marked this standard customer field read-only AND it actually
   *  resolved to a value (from prefill / URL param / restored draft). A read-only field with
   *  no value is never locked: locking an empty required field either dead-ends the customer
   *  (text inputs stay invalid and un-typeable) or silently submits an empty "required" value
   *  (disabled controls drop out of validation). Lock only what has a value to protect.
   *  `addressPart` distinguishes the three address sub-inputs, which share the 'address' key. */
  isCustomerFieldReadonly(field: string, addressPart?: 'street' | 'zip' | 'city'): boolean {
    if ((this.customerReadonlyFields?.indexOf(field) ?? -1) === -1) {
      return false;
    }
    return this.customerFieldHasValue(field, addressPart);
  }

  /** Whether the resolved UserInfo value backing a standard customer field is non-empty. */
  private customerFieldHasValue(field: string, addressPart?: 'street' | 'zip' | 'city'): boolean {
    switch (field) {
      case 'address':
        if (addressPart === 'zip') { return this.hasValue(this.userInfo.zip); }
        if (addressPart === 'city') { return this.hasValue(this.userInfo.city); }
        return this.hasValue(this.userInfo.street);
      case 'birthday':
        return this.userInfo.birthday != null;
      case 'notification_preferences':
        return this.hasValue(this.userInfo.customer_notification_preference);
      case 'title':
        return this.userInfo.title != null;
      case 'country':
        return this.userInfo.country != null;
      case 'meeting_types':
        return this.userInfo.meeting_type_id != null;
      case 'policy':
        // Legal consent must ALWAYS remain an active, editable action — never lock it, even when
        // a partner lists 'policy' in customer_readonly. A pre-ticked + disabled consent box would
        // submit as "consented" with zero user action, which is invalid consent under GDPR
        // (CJEU C-673/17 Planet49). Returning false keeps the checkbox interactive regardless.
        return false;
      default: {
        const prop = this.prefillFieldMap[field];
        return prop ? this.hasValue(this.userInfo[prop]) : false;
      }
    }
  }

  private hasValue(value: unknown): boolean {
    return value != null && String(value).trim().length > 0;
  }

  /** Seed standard fields from the partner-configured prefill defaults. A value already
   *  present (e.g. from a URL query param or restored draft) wins, so prefill only fills
   *  the gaps. Each value is stored stringified in customer_prefill and coerced to the
   *  UserInfo property's real type here (birthday → Date, ids → number). The 'policy' consent
   *  is deliberately excluded — see the comment at the end of this method. */
  private applyCustomerPrefill(): void {
    const prefill = this.customerPrefillValues;
    if (!prefill) {
      return;
    }

    // String fields → direct UserInfo props.
    for (const field of Object.keys(this.prefillFieldMap)) {
      const prop = this.prefillFieldMap[field];
      const value = prefill[field];
      if (prop && value && !this.userInfo[prop]) {
        (this.userInfo[prop] as unknown) = value;
      }
    }

    // Address is stored as three flat keys mapping to same-named props.
    if (prefill['street'] && !this.userInfo.street) {
      this.userInfo.street = prefill['street'];
    }
    if (prefill['zip'] && !this.userInfo.zip) {
      this.userInfo.zip = prefill['zip'];
    }
    if (prefill['city'] && !this.userInfo.city) {
      this.userInfo.city = prefill['city'];
    }

    // Notification preference (email / sms / both) — only seed it when the widget actually
    // offers that channel. Available channels mirror setupNotificationType(): SMS-only when
    // the email field isn't collected, otherwise email/sms/both. Seeding an unavailable
    // channel would leave the value with no matching ng-select option, so skip it and let
    // setupNotificationType() assign its valid default.
    if (prefill['notification_preferences'] && !this.userInfo.customer_notification_preference) {
      const availableNotificationChannels = this.customerFormFields?.indexOf('email') === -1
        ? ['sms']
        : ['email', 'sms', 'both'];
      if (availableNotificationChannels.includes(prefill['notification_preferences'])) {
        this.userInfo.customer_notification_preference = prefill['notification_preferences'];
      }
    }

    // Numeric ids stored as strings.
    if (prefill['title'] && this.userInfo.title == null) {
      const title = Number(prefill['title']);
      if (!Number.isNaN(title)) {
        this.userInfo.title = title;
      }
    }
    if (prefill['country'] && this.userInfo.country == null) {
      const country = Number(prefill['country']);
      if (!Number.isNaN(country)) {
        this.userInfo.country = country;
      }
    }
    if (prefill['meeting_types'] && this.userInfo.meeting_type_id == null) {
      const meetingTypeId = Number(prefill['meeting_types']);
      if (!Number.isNaN(meetingTypeId)) {
        this.userInfo.meeting_type_id = meetingTypeId;
      }
    }

    // Birthday stored as DD.MM.YYYY → Date (strict parse, ignore malformed values).
    if (prefill['birthday'] && !this.userInfo.birthday) {
      const birthday = moment(prefill['birthday'], DATE_FORMAT, true);
      if (birthday.isValid()) {
        this.userInfo.birthday = birthday.toDate();
      }
    }

    // Policy/consent is intentionally NOT prefilled: pre-ticking a GDPR consent checkbox is
    // invalid consent (CJEU C-673/17 Planet49). The customer must actively tick it themselves,
    // so the consent value is never seeded from partner config.
  }

  ngOnInit(): void {
    // Seed prefill defaults first so a configured notification_preferences default is in
    // place before setupNotificationType() applies its system fallback (partner default
    // wins over the system default, while a real customer value still wins over both).
    this.applyCustomerPrefill();
    this.setupNotificationType();
    this.setupZipCodeSearch();

    if (this.widgetService.widgetConf?.context?.smart_widget_birthday_minimum_age_18) {
      this.maximumPossibleDateForDatePicker = moment().subtract(18, 'years').toDate();
    }

    if (this.meetingTypes?.length > 1) {
      this.getMeetingTypes(this.meetingTypes);
    } else if (this.meetingTypes?.length === 1) {
      this.userInfo.meeting_type_id = this.meetingTypes[0];
    }

    if (this.customerFormFields?.length > 0) {
      const customFieldIds: number[] = [];

      this.fieldTypes = this.customerFormFields.map((item: any) => {
        if (typeof item === 'number') {
          customFieldIds.push(item);
          return { type: 'CUSTOM_FIELD', value: item };
        } else {
          return { type: item, value: item };
        }
      });

      if (customFieldIds?.length > 0) {
        // [Finding A] Reuse already-resolved question objects (service auto-load) instead of
        // redundantly re-fetching them via get_by_ids — only ids NOT covered by
        // preResolvedQuestions() go over the network. preResolvedQuestions() is empty for every
        // existing caller (dedicated_step, flag OFF), so remainingIds === customFieldIds there and
        // this reduces to the original single getCustomFieldsByIds(customFieldIds) call,
        // byte-for-byte (backward-compat).
        const preResolvedById = new Map<number, CustomFieldModel>(
          this.preResolvedQuestions().map(question => [question.id, question])
        );
        const remainingIds = customFieldIds.filter(id => !preResolvedById.has(id));
        const hasPreResolvedMatches = remainingIds.length < customFieldIds.length;

        if (hasPreResolvedMatches) {
          const preResolvedMatches = customFieldIds
            .filter(id => preResolvedById.has(id))
            .map(id => preResolvedById.get(id) as CustomFieldModel);
          this.applyCustomFieldsToWidgetQuestions(preResolvedMatches, false);
        }

        if (remainingIds.length > 0) {
          this.getCustomFieldsByIds(remainingIds, hasPreResolvedMatches);
        } else {
          // Every numeric id was already resolved — render immediately; do NOT block
          // showPersonalDataForm on a get_by_ids fetch that isn't needed.
          this.showGhostElement = false;
          this.showPersonalDataForm = true;
          this.checkCaptcha();
        }
      } else {
        this.showPersonalDataForm = true;
        this.showGhostElement = false;
        this.checkCaptcha();
      }
    } else {
      // TODO Handle no field is available in the configuration
    }

    this.findSelectedStore();
    if (this.fieldTypes.findIndex(fields => fields.type === 'country') > 0) {
      this.getCountries();
    }
  }

  checkCaptcha(): void {
    // timer + takeUntilDestroyed: the widget is never created after teardown (avoids the orphaned-iframe crash).
    timer(50).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.bookingService.partnerData?.is_captcha_feature_enabled && this.bookingService.partnerData?.captcha_type === CAPTCHA_TYPE.FRIENDLY) {
        this.bookingService.selectedBookingData.selectedUser.friendly_captcha = '';

        // Web component: the integrator page's CSP decides what may be framed. Bank CSPs allow the
        // Smart Widget origin but not *.frcapi.com, so the SDK runs in a frame served from our origin.
        if (this.widgetService.isWebComponentMode) {
          this.captchaFrame?.destroy();
          this.captchaFrame = new FriendlyCaptchaFrame({
            element: this.friendlyCaptchaRef.nativeElement,
            sitekey: this.bookingService.partnerData.friendly_captcha_sitekey,
            language: this.lang.split('_')[0],
            onComplete: response => this.onFriendlyCaptchaComplete(response),
            onError: error => this.onFriendlyCaptchaError(error)
          });
          return;
        }

        this.captchaWidgetHandle = this.friendlyCaptchaSDK.createWidget({
          element: this.friendlyCaptchaRef?.nativeElement,
          sitekey: this.bookingService.partnerData.friendly_captcha_sitekey,
          language: this.lang.split('_')[0]
        });

        this.captchaWidgetHandle.addEventListener('frc:widget.complete', (event: FRCWidgetCompleteEvent) => {
          this.onFriendlyCaptchaComplete(event.detail.response);
        });

        this.captchaWidgetHandle.addEventListener('frc:widget.error', (event: FRCWidgetWidgetErrorEvent) => {
          this.onFriendlyCaptchaError(event.detail.error);
        });
      }
    });
  }

  private onFriendlyCaptchaComplete(response: string): void {
    this.bookingService.selectedBookingData.selectedUser.friendly_captcha = response;
    this.friendlyCaptchaValidationRequired = false;
  }

  private onFriendlyCaptchaError(error: unknown): void {
    this.bookingService.selectedBookingData.selectedUser.friendly_captcha = '';
    this.friendlyCaptchaValidationRequired = false;
    LoggerService.warn('Captcha error:', error);
  }

  findSelectedStore(): void {
    const selectedStoreId = this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedStoreId;
    if (selectedStoreId) {
      this.selectedStore = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore;
    }
  }

  setupNotificationType(): void {
    if (this.customerFormFields?.indexOf('email') === -1) {
      this.NOTIFICATION_TYPES = [{
        display: 'sms',
        value: 'sms'
      }];
      if (this.widgetService.widgetConf?.context?.ask_for_customer_notification_preference) {
        if (!this.userInfo.customer_notification_preference) {
          this.userInfo.customer_notification_preference = 'sms';
        }
      }
    } else {
      this.NOTIFICATION_TYPES = [{
        display: 'email',
        value: 'email'
      }, {
        display: 'sms',
        value: 'sms'
      }, {
        display: 'both_sms_email',
        value: 'both'
      }];
      if (this.widgetService.widgetConf?.context?.ask_for_customer_notification_preference) {
        if (!this.userInfo.customer_notification_preference) {
          this.userInfo.customer_notification_preference = 'email';
        }
      }
    }
  }

  getMeetingTypes(ids: number[]): void {
    this.bookingService.getMeetingTypesByIds(ids).subscribe({
      next: (result: MeetingTypesModel[]) => {
        LoggerService.log('result is ', result);
        // TODO (Chirag): Remove this after calenso meeting type is added to the backend
        this.rawMeetingTypes = result.filter(meeting => meeting.id !== 3);
        this.supportedMeetingTypeIds = this.bookingService.getSupportedMeetingTypeIdsFromLocalStorage(
          this.bookingService.partnerData.booking_name
        );
        this.supportedMeetingTypeIds?.length && (this.rawMeetingTypes = this.rawMeetingTypes.filter(meeting => this.supportedMeetingTypeIds.includes(meeting.id)));

        if (this.userInfo.meeting_type_id) {
          this.userInfo.meetingType = this.rawMeetingTypes.find(meetingType => meetingType.id === this.userInfo.meeting_type_id);
        }
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.log(error);
      }
    });
  }

  previousQuestion(): void {
    this.previousEvent.emit();
  }

  markFormGroupTouched(form: NgForm): void {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  submit(personalForm: NgForm): void {
    if (this.customFieldsService.imageUploaded === false) {
      this.markFormGroupTouched(personalForm);
      if (this.widgetService.isWebComponentMode) {
        this.hostPageScrollService.cancel();
      }
      this.helperService.scrollToFirstInvalidControl(this.elementRef.nativeElement);
      return;
    }

    if (personalForm.invalid) {
      // Immediate feedback for anything genuinely broken (unchanged behaviour) …
      this.markFormGroupTouched(personalForm);
      const nonPhoneInvalid = Object.entries(personalForm.controls)
        .some(([name, control]) => control.invalid && name !== 'mobile' && name !== 'phone');
      // cancel() also disarms, so only an error the customer must fix and submit again drops a pending
      // host-page scroll. The phone settle path below completes the submit on its own and needs the arm.
      if (this.widgetService.isWebComponentMode && nonPhoneInvalid && !this.validitySettleInFlight) {
        this.hostPageScrollService.cancel();
      }
      this.helperService.scrollToFirstInvalidControl(this.elementRef.nativeElement);
      // … but the invalidity is often transient: the PHONE control re-validates
      // asynchronously after blur and reports INVALID for ~2s although its value is fine —
      // a submit landing in that window used to be swallowed as a dead click. ONLY for that
      // case (phone the sole invalid control, or a pure aggregation gap with no invalid
      // control) watch the status for one short window and complete the submit the user
      // explicitly asked for once the form turns valid. A genuinely-invalid submit (any
      // OTHER control invalid) keeps feedback-only behaviour — fixing a field never
      // auto-submits.
      if (nonPhoneInvalid || this.validitySettleInFlight) {
        return;
      }
      this.validitySettleInFlight = true;
      race(
        personalForm.statusChanges?.pipe(filter(() => personalForm.valid === true)) ?? timer(0),
        timer(3_000),
      )
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.validitySettleInFlight = false;
          // A direct re-click may have completed the submit while this watch was armed.
          if (this.isSubmitButtonDisabled || personalForm.invalid) {
            return;
          }
          this.finalizeSubmit(personalForm);
        });
      return;
    }

    this.finalizeSubmit(personalForm);
  }

  private finalizeSubmit(personalForm: NgForm): void {
    // A submit already went through (settle-watch or fast path) — never emit twice.
    if (this.isSubmitButtonDisabled) {
      return;
    }
    if (Number(this.bookingService.partnerData?.is_captcha_feature_enabled) === 1) {
      if (this.bookingService.partnerData?.captcha_type === CAPTCHA_TYPE.FRIENDLY) {
        if (!this.bookingService.selectedBookingData.selectedUser.friendly_captcha) {
          this.friendlyCaptchaValidationRequired = true;
          return;
        }
      }
    }

    this.isSubmitButtonDisabled = true;
    const widgetQuestions: CustomFieldModel[] = Object.values(this.widgetQuestions);
    if (widgetQuestions?.length > 0) {
      this.submitEvent.emit({
        userInfo: this.userInfo,
        personalFormWidgetQuestions: widgetQuestions,
        hiddenCustomFields: this.hiddenCustomFields
      });
    } else {
      this.submitEvent.emit({
        userInfo: this.userInfo,
        hiddenCustomFields: this.hiddenCustomFields,
        personalFormWidgetQuestions: []
      });
    }
  }

  getCustomFieldsByIds(customFieldIds: number[], mergeIntoExisting = false): void {
    this.customFieldsService.getCustomFieldsByIds(customFieldIds).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: CustomFieldModel[]) => {
        LoggerService.log('Inside getCustomFieldsByIds result ', result);
        this.applyCustomFieldsToWidgetQuestions(result, mergeIntoExisting);
        this.showGhostElement = false;
        this.showPersonalDataForm = true;
        this.checkCaptcha();
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.log('Error while fetching custom fields ', error);
        this.showGhostElement = false;
        if (error.status === 404) {
          // stop the widget process. Misconfiguration issue
          this.errorTitle = 'error_messages.misconfiguration_error_title';
          this.errorMessage = 'error_messages.misconfiguration_error_message';
          this.showError = true;
        }
      }
    });
  }

  // [Finding A] Shared post-processing for both the fetched (getCustomFieldsByIds) and
  // pre-resolved (preResolvedQuestions()) paths: routes is_hidden entries to hiddenCustomFields,
  // sorts select/radio options, then writes the visible remainder into widgetQuestions.
  // mergeIntoExisting=true folds into whatever is already in widgetQuestions this cycle (used when
  // a pre-resolved subset was already applied) instead of resetting it. mergeIntoExisting=false
  // (the default, used by every existing caller) is byte-for-byte the ORIGINAL single-fetch logic.
  private applyCustomFieldsToWidgetQuestions(result: CustomFieldModel[], mergeIntoExisting: boolean): void {
    if (result && result.length > 0) {
      for (const cf of result) {
        if (cf.is_hidden === 1) {
          this.hiddenCustomFields.push(cf);
        }
      }

      result = result.filter((item: CustomFieldModel) => {
        return item.is_hidden === 0;
      });

      for (const customField of result) {
        if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
          || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO
          || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD
          || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
          || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
          if (customField.alphabetically_sorted === 1) {
            customField.custom_field_options = this.helperService
              .sortByNumber(customField.custom_field_options, 'label');
          } else {
            customField.custom_field_options = this.helperService
              .sortByNumber(customField.custom_field_options, 'position');
          }
        }
      }

      LoggerService.log('custom fields result ', result);

      const widgetQuestions: { [key: number]: CustomFieldModel } = mergeIntoExisting ? { ...this.widgetQuestions } : {};

      for (const customField of result) {
        // Honor the per-widget required list additively over the question's own definition-level
        // `required`. Clone before overriding so a shared/pre-resolved question object (the auto-load
        // personal_form path reuses BookingService's resolved questions) is never mutated in place.
        const resolved = this.requiredQuestionIds?.includes(customField.id) && customField.required !== 1
          ? { ...customField, required: 1 }
          : customField;
        widgetQuestions[customField.id] = resolved;
      }
      this.widgetQuestions = widgetQuestions;
    } else if (!mergeIntoExisting) {
      this.widgetQuestions = {};
    }
  }

  // Fired when a sibling store-select question's preSelectStore() dropped an already-given
  // worker-select answer (the advisor does not serve the newly-chosen store). Every question is
  // mounted at once here, so there is nothing to rewind — but the worker ng-select would keep
  // displaying the old advisor while the answer is already gone from selectedCustomFieldValues.
  // Clearing it keeps what the customer sees equal to what will be submitted.
  onWorkerSelectAnswerInvalidated(questionId?: number): void {
    clearWorkerSelectAnswer(Object.values(this.widgetQuestions), questionId);
  }

  onRadioButtonChangeEvent(event: {
    customField: CustomFieldModel,
    customFieldOption: CustomFieldOptionModel
  }): void {
    LoggerService.log('event?.customFieldOption?.dependencies ', event?.customFieldOption?.dependencies);
    if (event?.customFieldOption?.dependencies && event?.customFieldOption?.dependencies?.trim() !== '') {
      const dependencies: string[] = event.customFieldOption.dependencies.split(',');
      const cfDependencies: number[] = dependencies.map(i => Number(i));
      this.getCustomFieldDependencies(
        event.customField,
        cfDependencies
      );
    }
  }

  getCustomFieldDependencies(
    customFieldData: CustomFieldModel,
    cfDependencies: number[],
  ): void {
    if (!this.widgetQuestions[customFieldData.id]) {
      this.widgetQuestions[customFieldData.id] = customFieldData;
    }
    LoggerService.log('this.widgetQuestions ', this.widgetQuestions);
    LoggerService.log('customFieldData ', customFieldData);
    this.widgetQuestions[customFieldData.id].showDependenciesGhostElement = true;
    this.customFieldsService
      .getCustomFieldsByIds(cfDependencies).subscribe({
        next: (result: CustomFieldModel[]) => {
          this.widgetQuestions[customFieldData.id].showDependenciesGhostElement = false;
          if (result && result.length > 0) {
            // result = this.helperService.sortByNumber(result, 'position');
            for (const customField of result) {
              if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
                || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
                if (customField.alphabetically_sorted === 1) {
                  customField.custom_field_options = this.helperService
                    .sortByNumber(customField.custom_field_options, 'label');
                } else {
                  customField.custom_field_options = this.helperService
                    .sortByNumber(customField.custom_field_options, 'position');
                }
              }
            }

            result = result.filter((item: CustomFieldModel) => {
              return item.is_hidden === 0;
            });

            this.widgetQuestions[customFieldData.id].customFieldDependencies = result;
          } else {
            // Handle this case if dependencies are undefined
          }
        },
        error: () => {
          LoggerService.log('Error while fetching custom fields');
          this.widgetQuestions[customFieldData.id].showDependenciesGhostElement = false;
        }
      });
  }

  onMeetingTypeChange(meetingType: MeetingTypesModel): void {
    this.userInfo.meetingType = meetingType;
  }

  getCountries(): void {
    this.countryHelperService.getCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: countries => {
          this.countries = this.countryHelperService.createNewCountries(countries);
        }
      });
  }

  private setupZipCodeSearch(): void {
    if (this.widgetService.widgetConf?.partner?.is_zip_codes_search_enabled === 1) {
      this.searchZip$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((zip: string) => {
          if (zip?.length >= 3) {
            return this.bookingService.getCitiesByZip(zip);
          }
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (cities) => this.citySuggestions = cities,
        error: () => this.citySuggestions = []
      });

      // Trigger search for predefined ZIP code
      if (this.userInfo?.zip?.length >= 3) {
        this.searchZip$.next(this.userInfo.zip);
      }
    }
  }

  protected onZipBlur(): void {
    if (this.userInfo.zip) {
      this.userInfo.zip = this.userInfo.zip.trim().toUpperCase().replace(/\s+/g, ' ');
    }
  }

  protected onZipInput(zip: string): void {
    if (this.widgetService.widgetConf?.partner?.is_zip_codes_search_enabled === 1) {
      // Skip API call if value is from datalist selection (contains parentheses)
      if (zip && /\s+\(.+\)$/.test(zip)) {
        return;
      }
      this.searchZip$.next(zip);
    }
  }

  protected onZipSuggestionSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedValue = input.value;

    // Parse "ZIP (CityName)" format
    const match = selectedValue.match(/^(\S+)\s+\((.+)\)$/);
    if (match) {
      const zip = match[1];
      const cityName = match[2].trim();
      const selectedCity = this.citySuggestions.find(city => city.zip === zip && city.city18 === cityName);
      if (selectedCity) {
        this.userInfo.zip = selectedCity.zip;
        this.userInfo.city = selectedCity.city18;
      }
    }
  }
}
